import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  embedToForm,
  emptyForm,
  formToEmbed,
  type DiscordEmbed,
  type EmbedFormState,
} from '@raynex/discord-ui';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DiscordChannelPicker } from '@/components/discord/DiscordPickers';
import { ApiError } from '@/lib/api';
import {
  usePublishTicketPanel,
  useRegeneratePanelLookupSecret,
  useTicketCategories,
  useTicketPanels,
  useUpdateTicketPanel,
} from '@/lib/queries';

export function PanelDetailPage() {
  const { id = '' } = useParams();
  const panels = useTicketPanels();
  const cats = useTicketCategories();
  const update = useUpdateTicketPanel();
  const publish = usePublishTicketPanel();

  const panel = panels.data?.find((p) => p.id === id);

  const [channelId, setChannelId] = useState('');
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [embedForm, setEmbedForm] = useState<EmbedFormState>(emptyForm());
  const [lookupUrl, setLookupUrl] = useState('');
  const [newLookupSecret, setNewLookupSecret] = useState<string | null>(null);
  const regenLookup = useRegeneratePanelLookupSecret();

  useEffect(() => {
    if (!panel) return;
    setChannelId(panel.channelId);
    setSelectedCats(new Set(panel.categoryIds));
    setEmbedForm(embedToForm(panel.embed as DiscordEmbed));
    setLookupUrl(panel.customerLookupUrl ?? '');
  }, [panel]);

  function toggleCat(catId: string) {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }

  if (panels.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!panel) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/tickets/panels">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Link>
      </Button>
    );
  }

  async function handleSave(): Promise<boolean> {
    try {
      await update.mutateAsync({
        id,
        patch: {
          channelId,
          embed: formToEmbed(embedForm) as Record<string, unknown>,
          categoryIds: Array.from(selectedCats),
          customerLookupUrl: lookupUrl || null,
        },
      });
      toast.success('Saved');
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
      return false;
    }
  }

  async function handlePublish() {
    const ok = await handleSave();
    if (!ok) return;
    try {
      await publish.mutateAsync(id);
      toast.success('Publish command sent to the bot');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tickets/panels">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to list
          </Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSave} disabled={update.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
          <Button onClick={handlePublish} disabled={publish.isPending || update.isPending}>
            <Send className="mr-2 h-4 w-4" />
            Save + Publish
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Target channel</Label>
            <DiscordChannelPicker value={channelId} onValueChange={setChannelId} />
          </div>
          <div className="space-y-2">
            <Label>Category buttons</Label>
            <div className="space-y-1 rounded-md border border-input p-3">
              {cats.data?.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedCats.has(c.id)}
                    onChange={() => toggleCat(c.id)}
                  />
                  {c.emoji && <span>{c.emoji}</span>}
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Embed message</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EmbedEditor value={embedForm} onChange={setEmbedForm} />
            <div className="sticky top-4 self-start">
              <DiscordEmbedPreview embed={formToEmbed(embedForm)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer lookup</CardTitle>
          <CardDescription>
            When an admin clicks "Customer info" in a ticket opened from this panel, the bot POSTs{' '}
            <code>{`{ "discord_id": "..." }`}</code> to this URL with an HMAC-SHA256 signature in{' '}
            <code>X-Raynex-Signature</code>. The JSON response is displayed as the customer card.
            Leave empty to disable.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="lookupUrl">URL (https://...)</Label>
            <Input
              id="lookupUrl"
              type="url"
              value={lookupUrl}
              onChange={(e) => setLookupUrl(e.target.value)}
              placeholder="https://example.com/raynex/customer"
            />
          </div>
          <div className="space-y-1">
            <Label>Secret last rotated</Label>
            <p className="text-sm text-muted-foreground">
              {panel.customerLookupSecretLastRotatedAt
                ? new Date(panel.customerLookupSecretLastRotatedAt).toLocaleString()
                : 'never'}
            </p>
          </div>
          {panel.customerLookupUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!confirm('Regenerate panel lookup secret? Old secret stops working.')) return;
                const res = await regenLookup.mutateAsync(id);
                setNewLookupSecret(res.customerLookupSecret);
              }}
            >
              Regenerate secret
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(newLookupSecret)} onOpenChange={(o) => !o && setNewLookupSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New secret</DialogTitle>
            <DialogDescription>Copy now — won't be shown again.</DialogDescription>
          </DialogHeader>
          <Input
            readOnly
            value={newLookupSecret ?? ''}
            onFocus={(e) => e.currentTarget.select()}
            className="my-3"
          />
          <DialogFooter>
            <Button onClick={() => setNewLookupSecret(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
